import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User,
  ShieldCheck,
  Calendar,
  Lock,
  FloppyDisk,
  PencilSimple,
  Storefront,
  Envelope,
  CheckCircle,
  CircleNotch,
  WarningCircle,
} from '@phosphor-icons/react';
import { apiClient, useAuthStore, useNotificationStore } from '@dukene/shared';
import { format } from 'date-fns';
import { getAvatarForUser } from '@/utils/avatar';

interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  emailVerified: boolean;
  status: string;
  storeId: string | null;
  createdAt: string;
  updatedAt: string;
  store?: {
    id: string;
    name: string;
    subdomain: string;
    status: string;
    logo: string | null;
  };
  ownedStores?: Array<{
    id: string;
    name: string;
    subdomain: string;
    status: string;
    logo: string | null;
    accountPlan?: string;
    accountStatus?: string;
  }>;
  stores?: Array<{
    id: string;
    name: string;
    subdomain: string;
    status: string;
    logo: string | null;
    accountName?: string;
    accountPlan?: string;
    accountStatus?: string;
    membershipRole?: string;
  }>;
  account?: {
    id: string;
    name: string;
    plan: string;
    billingEmail: string;
    status: string;
  };
  ownedOrganisation?: {
    id: string;
    name: string;
    plan: string;
    billingEmail: string;
    status: string;
  } | null;
  membershipRole?: string;
  organisationName?: string;
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export default function Profile() {
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState<Partial<UserProfile>>({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState<PasswordForm>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const { resendVerification, activeStoreId, setActiveStore } = useAuthStore();
  const { addNotification } = useNotificationStore();
  const queryClient = useQueryClient();

  // Fetch current user profile
  const {
    data: profile,
    isLoading,
    error,
  } = useQuery<UserProfile>({
    queryKey: ['user-profile'],
    queryFn: () => apiClient.get<UserProfile>('/auth/me'),
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: Partial<UserProfile>) => apiClient.patch('/auth/me', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      setIsEditing(false);
      setEditedProfile({});
      addNotification({
        type: 'success',
        title: 'Profile Saved',
        message: 'Your personal account details have been updated.',
      });
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      apiClient.post('/auth/change-password', data),
    onSuccess: () => {
      setIsChangingPassword(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordError('');
      window.location.href = '/login';
    },
    onError: (err: any) => {
      setPasswordError(
        err?.response?.data?.error?.message || 'Failed to update security password'
      );
    },
  });

  const handleEdit = () => {
    setIsEditing(true);
    setEditedProfile({
      firstName: profile?.firstName,
      lastName: profile?.lastName,
      email: profile?.email,
      organisationName: profile?.ownedOrganisation?.name,
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedProfile({});
  };

  const handleSave = () => {
    updateProfileMutation.mutate(editedProfile);
  };

  const handlePasswordChange = () => {
    setPasswordError('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New password and confirmation do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }

    changePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await resendVerification();
      addNotification({
        type: 'success',
        title: 'Verification Email Sent',
        message: 'A new verification link has been sent to your inbox.',
      });
    } catch (err: any) {
      addNotification({
        type: 'error',
        title: 'Send Failed',
        message: err.message || 'Could not resend email verification.',
      });
    } finally {
      setResendLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-16 text-xs text-[#5C5F62]">
        <CircleNotch weight="bold" className="w-6 h-6 animate-spin mr-2 text-[#006838]" />
        Loading account profile...
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="p-10 text-center text-xs text-red-600">
        Failed to load account profile data.
      </div>
    );
  }

  // `stores` is the normalized, access-scoped list and includes organisation plan metadata.
  const allStores = profile.stores || profile.ownedStores || [];
  const ownedOrganisation = profile.ownedOrganisation;

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Polaris High-Density Top Card */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 border border-[#D2D5D9] overflow-hidden bg-emerald/10 shadow-sm">
            <img src={getAvatarForUser(profile.id || profile.email)} alt="Avatar" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base font-bold text-[#202223] dark:text-white">
                {profile.firstName} {profile.lastName}
              </h1>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#F6F6F7] dark:bg-slate-800 text-[#5C5F62] uppercase">
                {profile.role || 'MERCHANT'}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
              <span className="inline-flex items-center gap-1">
                <Envelope weight="bold" className="w-3.5 h-3.5" />
                {profile.email}
              </span>
              <span>•</span>
              <span className="inline-flex items-center gap-1">
                <Calendar weight="bold" className="w-3.5 h-3.5" />
                Member since{' '}
                {profile.createdAt
                  ? format(new Date(profile.createdAt), 'MMM yyyy')
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {!isEditing ? (
          <button
            type="button"
            onClick={handleEdit}
            className="h-8 px-3.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white inline-flex items-center gap-1.5 transition-colors shrink-0"
          >
            <PencilSimple weight="bold" className="w-3.5 h-3.5 text-[#5C5F62]" />
            Edit Profile
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={updateProfileMutation.isPending}
              className="h-8 px-3.5 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold inline-flex items-center gap-1.5 transition-colors disabled:opacity-50"
            >
              {updateProfileMutation.isPending ? (
                <CircleNotch weight="bold" className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
              )}
              Save Details
            </button>
          </div>
        )}
      </div>

      {/* Verification Warning Strip */}
      {!profile.emailVerified && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 text-xs text-amber-800 dark:text-amber-300">
            <WarningCircle weight="fill" className="w-4 h-4 text-amber-600 shrink-0" />
            <span>
              Your account email address is not verified yet. Verify to prevent account access interruptions.
            </span>
          </div>
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={resendLoading}
            className="h-7 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold shrink-0"
          >
            {resendLoading ? 'Sending...' : 'Resend Verification Email'}
          </button>
        </div>
      )}

      {/* 2-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Left Column (Personal Info & Security) */}
        <div className="lg:col-span-2 space-y-4">
          {/* Profile Form */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62] flex items-center gap-1.5">
              <User weight="bold" className="w-4 h-4 text-[#006838]" />
              Personal Information
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                  First Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedProfile.firstName || ''}
                    onChange={(e) =>
                      setEditedProfile({ ...editedProfile, firstName: e.target.value })
                    }
                    className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
                  />
                ) : (
                  <div className="h-9 px-3 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 flex items-center text-xs font-medium text-[#202223] dark:text-white">
                    {profile.firstName}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                  Last Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedProfile.lastName || ''}
                    onChange={(e) =>
                      setEditedProfile({ ...editedProfile, lastName: e.target.value })
                    }
                    className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
                  />
                ) : (
                  <div className="h-9 px-3 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 flex items-center text-xs font-medium text-[#202223] dark:text-white">
                    {profile.lastName}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                  Email Address
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    value={editedProfile.email || ''}
                    onChange={(e) =>
                      setEditedProfile({ ...editedProfile, email: e.target.value })
                    }
                    className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
                  />
                ) : (
                  <div className="h-9 px-3 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 flex items-center justify-between text-xs font-medium text-[#202223] dark:text-white">
                    <span>{profile.email}</span>
                    {profile.emailVerified && (
                      <span title="Verified">
                        <CheckCircle weight="fill" className="w-4 h-4 text-[#006838]" />
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                  Organization / Company
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedProfile.organisationName || ''}
                    disabled={!profile.ownedOrganisation}
                    onChange={(e) =>
                      setEditedProfile({
                        ...editedProfile,
                        organisationName: e.target.value,
                      })
                    }
                    className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                ) : (
                  <div className="h-9 px-3 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 flex items-center text-xs font-medium text-[#202223] dark:text-white">
                    {profile.ownedOrganisation?.name || 'No owned organisation'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Account Security Card */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62] flex items-center gap-1.5">
                <Lock weight="bold" className="w-4 h-4 text-[#006838]" />
                Account Security & Password
              </h3>

              {!isChangingPassword && (
                <button
                  type="button"
                  onClick={() => setIsChangingPassword(true)}
                  className="h-7 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white transition-colors"
                >
                  Change Password
                </button>
              )}
            </div>

            {isChangingPassword && (
              <div className="p-4 rounded-xl bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 space-y-3">
                {passwordError && (
                  <div className="p-2.5 rounded-lg bg-red-50 text-red-700 border border-red-200 text-xs font-medium">
                    {passwordError}
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                    }
                    className="w-full h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(e) =>
                        setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                      }
                      className="w-full h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          confirmPassword: e.target.value,
                        })
                      }
                      className="w-full h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setIsChangingPassword(false);
                      setPasswordError('');
                    }}
                    className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 text-xs font-semibold text-[#202223] dark:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handlePasswordChange}
                    disabled={changePasswordMutation.isPending}
                    className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold"
                  >
                    {changePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column (Stores & Workspace) */}
        <div className="space-y-4">
          {/* Store Workspaces Card */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62] flex items-center gap-1.5">
              <Storefront weight="bold" className="w-4 h-4 text-[#006838]" />
              Merchant Stores ({allStores.length})
            </h3>

            {allStores.length === 0 ? (
              <p className="text-xs text-[#5C5F62]">No active stores attached to this account.</p>
            ) : (
              <div className="space-y-2">
                {allStores.map((st) => {
                  const isCurrent = st.id === activeStoreId;
                  return (
                    <div
                      key={st.id}
                      className={`p-3 rounded-xl border flex items-center justify-between transition-colors ${
                        isCurrent
                          ? 'border-[#006838] bg-[#E3F1DF]/20'
                          : 'border-[#E1E4E8] dark:border-slate-800'
                      }`}
                    >
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-xs text-[#202223] dark:text-white">
                            {st.name}
                          </span>
                          {isCurrent && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#006838] text-white">
                              ACTIVE
                            </span>
                          )}
                          {st.accountPlan && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                              {st.accountPlan} PLAN
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-[#5C5F62] block">
                          {st.subdomain}.dukene.co.ke
                        </span>
                      </div>

                      {!isCurrent && (
                        <button
                          type="button"
                          onClick={() => setActiveStore(st.id)}
                          className="h-7 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white"
                        >
                          Switch
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Organisations owned by this user */}
          {ownedOrganisation && (
            <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62] flex items-center gap-1.5">
                <ShieldCheck weight="bold" className="w-4 h-4 text-[#006838]" />
                Organisation Info
              </h3>

              <div className="p-3 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-xs text-[#202223] dark:text-white">
                      {ownedOrganisation.name}
                    </span>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#E3F1DF] text-[#006838]">
                      {ownedOrganisation.status}
                    </span>
                  </div>
                  <p className="text-[11px] font-bold text-[#006838] uppercase">
                    {ownedOrganisation.plan} PLAN
                  </p>
                  <p className="text-xs text-[#5C5F62]">
                    Billing Email: {ownedOrganisation.billingEmail}
                  </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
